import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, RefreshCw, AlertTriangle, FileText, Building2, Power, PowerOff, CheckCircle2, XCircle, HelpCircle, History as HistoryIcon, RotateCcw, X } from "lucide-react";

/**
 * Reviews → Yext Import — admin page.
 *
 * Gated at the router level to {owner, coo, cfo, boutique_manager}. Attribution
 * now flows from the SOURCE — every Yext account is 1:1 with a boutique. A GM
 * uploads into their own source automatically; owners select the target source.
 */

const TABS = [
  { key: "overview",  label: "Overview" },
  { key: "sources",   label: "Sources" },
  { key: "history",   label: "Import History" },
  { key: "mapping",   label: "Field Mapping" },
];

export default function YextImport() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/yext/status");
      setStatus(r.data);
    } catch (e) {
      toast.error("Not authorized" + (e?.response?.status === 403 ? "" : ` — ${e.message}`));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  if (loading) return <Shell><div className="text-sm text-stone-400">Loading…</div></Shell>;

  return (
    <Shell>
      <header className="mb-6">
        <h1 className="font-serif-display text-3xl text-stone-900" data-testid="yext-title">
          Yext Import
        </h1>
        <p className="mt-2 text-sm text-stone-500 max-w-2xl">
          Every Yext account belongs to one boutique. Files land in that
          boutique's folder, are validated, and reviews inherit the source's
          boutique — never the CSV row.
        </p>
      </header>

      <nav className="border-b border-stone-100 flex gap-1 mb-6" data-testid="yext-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`yext-tab-${t.key}`}
                  className={`px-3 py-2 text-sm rounded-t-md transition-colors ${
                    tab === t.key ? "text-stone-900 border-b-2 border-stone-900 -mb-px"
                                  : "text-stone-500 hover:text-stone-800"
                  }`}>{t.label}</button>
        ))}
      </nav>

      {tab === "overview" && <Overview status={status} refresh={loadStatus} />}
      {tab === "sources"  && <Sources status={status} refresh={loadStatus} />}
      {tab === "history"  && <History status={status} />}
      {tab === "mapping"  && <FieldMapping status={status} />}
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="px-8 py-8 max-w-6xl">{children}</div>;
}

// ============================================================
// Overview — status cards + upload widget
// ============================================================
function Overview({ status, refresh }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const viewer = status?.viewer || {};
  const sources = useMemo(() => status?.sources || [], [status]);
  const isGm = viewer.role === "boutique_manager";
  const gmSource = isGm ? sources.find((s) => s.boutique_code === viewer.boutique_code) : null;

  // Uploader picks a target boutique code:
  //  - GM: forced to their own; the picker is a read-only chip.
  //  - Owner/COO/CFO: must choose from enabled sources.
  const enabledOwnerSources = sources.filter((s) => s.enabled);
  const [chosenCode, setChosenCode] = useState(
    isGm ? (viewer.boutique_code || "") : (enabledOwnerSources[0]?.boutique_code || "")
  );

  const onUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only .csv files are accepted."); return;
    }
    if (!chosenCode) { toast.error("Choose a boutique source first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("boutique_code", chosenCode);
      // Always route through the preview flow — the mapping must be
      // explicitly confirmed before Toto touches production reviews.
      const r = await api.post("/yext/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onScan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/yext/imports/tick");
      toast.success(
        `Scan: ${r.data.sources_scanned} sources · ${r.data.scanned} files · `
        + `${r.data.imported} imported · ${r.data.duplicates} duplicates · `
        + `${r.data.failed} failed`
      );
      refresh();
    } catch (e) { toast.error(e.message); }
    finally { setScanning(false); }
  };

  const cfg = status?.scheduler || {};
  const last = status?.last_successful_import;
  const attempted = status?.last_attempted_import;

  return (
    <div>
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card testid="yext-card-scheduler" label="Scheduler">
          <Row k="Enabled" v={cfg.enabled ? "Yes" : "No"} />
          <Row k="Interval" v={`${cfg.interval_minutes} min`} />
          <Row k="Driver" v={cfg.driver} />
          <Row k="Sources" v={sources.length} />
        </Card>
        <Card testid="yext-card-last" label="Last successful import">
          {last ? (
            <>
              <Row k="File" v={last.filename} />
              <Row k="Boutique" v={last.boutique_code || "—"} />
              <Row k="+Inserted" v={last.inserted_count} />
              <Row k="~Updated" v={last.updated_count} />
            </>
          ) : <Empty msg="No successful imports yet." />}
        </Card>
        <Card testid="yext-card-attempt" label="Last attempted">
          {attempted ? (
            <>
              <Row k="File" v={attempted.filename} />
              <Row k="Boutique" v={attempted.boutique_code || "—"} />
              <Row k="Status" v={attempted.status} />
              {attempted.error_summary && <Row k="Error" v={attempted.error_summary} />}
            </>
          ) : <Empty msg="No imports attempted yet." />}
        </Card>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Stat testid="yext-stat-total" label="Total reviews" v={status?.total_reviews ?? 0} />
        <Stat testid="yext-stat-open-exc" label="Open exceptions" v={status?.open_exceptions ?? 0}
              tone={(status?.open_exceptions ?? 0) > 0 ? "attention" : "neutral"} />
        <Stat testid="yext-stat-provisional" label="Mapping" v={status?.field_mapping?.provisional ? "Provisional" : "Confirmed"}
              tone={status?.field_mapping?.provisional ? "attention" : "positive"} />
        <Stat testid="yext-stat-driver" label="Driver" v={cfg.driver} />
      </section>

      <section className="rounded-2xl border border-stone-100 bg-white p-5" data-testid="yext-uploader">
        <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-3">Upload a CSV</div>

        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="text-xs text-stone-500">Target boutique</span>
          {isGm ? (
            <span data-testid="yext-upload-boutique-chip"
                  className="inline-flex items-center gap-2 rounded-full bg-stone-100 text-stone-800 text-xs px-3 py-1">
              <Building2 size={12} />
              {gmSource ? `${gmSource.boutique_code} · ${gmSource.boutique_name}` : viewer.boutique_code}
            </span>
          ) : (
            <select value={chosenCode} onChange={(e) => setChosenCode(e.target.value)}
                    data-testid="yext-upload-boutique-select"
                    className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm">
              <option value="">Select boutique…</option>
              {enabledOwnerSources.map((s) => (
                <option key={s.boutique_code} value={s.boutique_code}>
                  {s.boutique_code} · {s.boutique_name}
                </option>
              ))}
            </select>
          )}
          {gmSource && !gmSource.enabled && (
            <span className="text-[11px] text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
              Source is currently disabled by leadership
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <label className="inline-flex">
            <input ref={fileRef} type="file" accept=".csv" className="hidden"
                   data-testid="yext-upload-input"
                   onChange={(e) => onUpload(e.target.files?.[0])} />
            <span onClick={() => fileRef.current?.click()}
                  data-testid="yext-upload-btn"
                  className={`inline-flex items-center gap-2 rounded-full bg-stone-900 text-white text-sm px-4 py-2 cursor-pointer hover:bg-stone-700 ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
              <Upload size={13} /> {uploading ? "Uploading…" : "Upload CSV"}
            </span>
          </label>
          <button onClick={onScan} disabled={scanning}
                  data-testid="yext-scan-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-stone-200 text-stone-700 text-sm px-4 py-2 hover:bg-stone-50">
            <RefreshCw size={13} className={scanning ? "animate-spin" : ""} /> Check for files now
          </button>
        </div>
      </section>

      {preview && (
        <PreviewModal
          preview={preview}
          viewer={viewer}
          sources={sources}
          onClose={() => setPreview(null)}
          onDone={() => { setPreview(null); refresh(); }}
        />
      )}
    </div>
  );
}

function Card({ label, testid, children }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-stone-100 bg-white p-5">
      <div className="text-[10px] font-medium uppercase tracking-wider text-stone-400 mb-2">{label}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between text-sm gap-3">
      <span className="text-stone-500">{k}</span>
      <span className="text-stone-900 truncate max-w-[60%] text-right">{v ?? "—"}</span>
    </div>
  );
}
function Empty({ msg }) { return <div className="text-xs text-stone-400">{msg}</div>; }
function Stat({ label, v, tone, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border p-5 ${tone === "attention" ? "border-amber-200 bg-amber-50/40" : tone === "positive" ? "border-emerald-100 bg-emerald-50/30" : "border-stone-100 bg-white"}`}>
      <div className="text-[10px] font-medium uppercase tracking-wider text-stone-400 mb-1">{label}</div>
      <div className="font-serif-display text-2xl text-stone-900">{v}</div>
    </div>
  );
}
function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

// ============================================================
// Sources — one row per boutique feed
// ============================================================
function Sources({ status, refresh }) {
  const canManage = !!status?.viewer?.can_manage_sources;
  const [rows, setRows] = useState(status?.sources || []);
  const [saving, setSaving] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/yext/sources");
    setRows(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (row) => {
    if (!canManage) return;
    setSaving(row.boutique_code);
    try {
      await api.post("/yext/sources", {
        boutique_code: row.boutique_code, enabled: !row.enabled,
      });
      toast.success(`${row.boutique_code} source ${!row.enabled ? "enabled" : "disabled"}`);
      await load(); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSaving(null); }
  };

  return (
    <div className="rounded-2xl border border-stone-100 bg-white overflow-hidden" data-testid="yext-sources-list">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-stone-400 bg-stone-50">
          <tr>
            <th className="text-left px-4 py-2">Boutique</th>
            <th className="text-left px-4 py-2">Folder</th>
            <th className="text-left px-4 py-2">Mapping</th>
            <th className="text-left px-4 py-2">Override</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-right px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan="6" className="px-4 py-6 text-stone-400 text-center">No sources configured.</td></tr>
          )}
          {rows.map((s) => (
            <tr key={s.boutique_code} className="border-t border-stone-50" data-testid={`yext-source-row-${s.boutique_code}`}>
              <td className="px-4 py-2">
                <div className="font-medium text-stone-900">{s.boutique_code}</div>
                <div className="text-xs text-stone-500">{s.boutique_name}</div>
              </td>
              <td className="px-4 py-2 font-mono text-xs text-stone-600">
                incoming/{s.folder_relative}/
              </td>
              <td className="px-4 py-2 text-xs">
                {s.mapping_confirmed_at ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`yext-source-mapping-${s.boutique_code}`}>
                    <CheckCircle2 size={12} /> Confirmed {fmt(s.mapping_confirmed_at)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-700" data-testid={`yext-source-mapping-${s.boutique_code}`}>
                    <AlertTriangle size={12} /> Not yet confirmed
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-xs">
                {Object.keys(s.field_mapping_override || {}).length === 0 ? (
                  <span className="text-stone-400">— uses default —</span>
                ) : (
                  <span className="text-stone-600">
                    {Object.entries(s.field_mapping_override).map(([k, v]) => `${k}→${v}`).join(", ")}
                  </span>
                )}
              </td>
              <td className="px-4 py-2">
                {s.enabled ? (
                  <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700" data-testid={`yext-source-status-${s.boutique_code}`}>Enabled</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-stone-100 text-stone-500" data-testid={`yext-source-status-${s.boutique_code}`}>Disabled</span>
                )}
              </td>
              <td className="px-4 py-2 text-right space-x-2">
                <button
                  onClick={() => setHistoryFor(s.boutique_code)}
                  data-testid={`yext-source-history-${s.boutique_code}`}
                  className="inline-flex items-center gap-1 rounded-full border border-stone-200 text-stone-700 text-xs px-3 py-1 hover:bg-stone-50">
                  <HistoryIcon size={11} /> History
                </button>
                {canManage && (
                  <button
                    onClick={() => toggle(s)}
                    disabled={saving === s.boutique_code}
                    data-testid={`yext-source-toggle-${s.boutique_code}`}
                    className="inline-flex items-center gap-1 rounded-full border border-stone-200 text-stone-700 text-xs px-3 py-1 hover:bg-stone-50">
                    {s.enabled ? <PowerOff size={11} /> : <Power size={11} />}
                    {s.enabled ? "Disable" : "Enable"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {historyFor && (
        <HistoryDrawer
          boutique_code={historyFor} viewer={status?.viewer || {}}
          onClose={() => setHistoryFor(null)}
          onRolledBack={() => { load(); refresh(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// History — imports list with boutique filter
// ============================================================
function History({ status }) {
  const canFilter = !!status?.viewer && status.viewer.role !== "boutique_manager";
  const sources = status?.sources || [];
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filterCode, setFilterCode] = useState("");

  const load = useCallback(async () => {
    const params = filterCode ? { params: { boutique_code: filterCode } } : {};
    const r = await api.get("/yext/imports", params);
    setRows(r.data);
  }, [filterCode]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {canFilter && (
        <div className="mb-4 flex items-center gap-2" data-testid="yext-history-filter">
          <span className="text-xs text-stone-500">Filter</span>
          <select value={filterCode} onChange={(e) => setFilterCode(e.target.value)}
                  data-testid="yext-history-filter-select"
                  className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm">
            <option value="">All boutiques</option>
            {sources.map((s) => (
              <option key={s.boutique_code} value={s.boutique_code}>{s.boutique_code} · {s.boutique_name}</option>
            ))}
          </select>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-stone-100 bg-white overflow-hidden" data-testid="yext-history-list">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-stone-400 bg-stone-50">
              <tr>
                <th className="text-left px-4 py-2">File</th>
                <th className="text-left px-4 py-2">Boutique</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Rows</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="4" className="px-4 py-6 text-stone-400 text-center">No imports yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setSelected(r)}
                    data-testid={`yext-import-row-${r.id}`}
                    className="border-t border-stone-50 hover:bg-stone-50 cursor-pointer">
                  <td className="px-4 py-2 truncate max-w-[180px]"><FileText size={11} className="inline mr-1 text-stone-400" />{r.filename}</td>
                  <td className="px-4 py-2 text-xs text-stone-600">{r.boutique_code || "—"}</td>
                  <td className="px-4 py-2"><StatusChip s={r.status} /></td>
                  <td className="px-4 py-2 text-right text-stone-500">{r.total_rows ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ImportDetail imp={selected} />
      </div>
    </div>
  );
}

function StatusChip({ s }) {
  const tone = {
    completed: "bg-emerald-50 text-emerald-700",
    completed_with_errors: "bg-amber-50 text-amber-700",
    failed: "bg-rose-50 text-rose-700",
    duplicate_file: "bg-stone-100 text-stone-500",
    in_progress: "bg-sky-50 text-sky-700",
    misconfigured: "bg-rose-50 text-rose-700",
  }[s] || "bg-stone-100 text-stone-500";
  return <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${tone}`}>{s}</span>;
}

function ImportDetail({ imp }) {
  const [details, setDetails] = useState(null);
  useEffect(() => {
    if (!imp) { setDetails(null); return; }
    api.get(`/yext/imports/${imp.id}`).then((r) => setDetails(r.data));
  }, [imp]);
  if (!imp) return <div className="rounded-2xl border border-stone-100 bg-white p-6 text-sm text-stone-400">Select an import to see details.</div>;
  return (
    <div className="rounded-2xl border border-stone-100 bg-white p-5" data-testid="yext-import-detail">
      <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Import</div>
      <div className="font-mono text-xs text-stone-600 mb-3 break-all">{imp.filename}</div>
      <div className="grid grid-cols-2 gap-1 text-sm mb-4">
        <Row k="Boutique" v={imp.boutique_code || "—"} />
        <Row k="Status" v={imp.status} />
        <Row k="Total rows" v={imp.total_rows} />
        <Row k="Inserted" v={imp.inserted_count} />
        <Row k="Updated" v={imp.updated_count} />
        <Row k="Duplicates" v={imp.duplicate_count} />
        <Row k="Rejected" v={imp.rejected_count} />
        <Row k="Collisions" v={imp.collision_count ?? 0} />
      </div>
      {imp.error_summary && <div className="text-xs bg-rose-50 text-rose-700 rounded-lg p-3 mb-3">{imp.error_summary}</div>}
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/yext/imports/${imp.id}/exceptions.csv`}
         className="text-xs text-stone-500 underline hover:text-stone-800"
         data-testid="yext-download-exceptions">Download exceptions CSV</a>
      {details?.exceptions?.length ? (
        <div className="mt-4 max-h-64 overflow-auto border-t border-stone-100 pt-3">
          {details.exceptions.slice(0, 20).map((e) => (
            <div key={e.id} className="text-xs py-1.5 border-b border-stone-50 flex items-start gap-2" data-testid={`yext-exc-${e.id}`}>
              <AlertTriangle size={11} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-stone-700">Row {e.row_number} · {e.exception_type}</div>
                <div className="text-stone-400">{e.exception_message}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ============================================================
// Field Mapping (system-wide default; per-source override lives on Sources tab)
// ============================================================
function FieldMapping({ status }) {
  const canEdit = !!status?.viewer?.can_manage_sources;
  const [doc, setDoc] = useState(null);
  const [confirmed, setConfirmed] = useState({});
  useEffect(() => {
    api.get("/yext/field-mapping").then((r) => {
      setDoc(r.data); setConfirmed(r.data.confirmed_mapping || {});
    });
  }, []);
  const save = async () => {
    try {
      await api.post("/yext/field-mapping", { confirmed_mapping: confirmed, provisional: false });
      toast.success("Mapping confirmed. Future imports will use these exact CSV headers.");
    } catch (e) { toast.error(e.message); }
  };
  if (!doc) return <div className="text-sm text-stone-400">Loading…</div>;
  return (
    <div>
      {doc.provisional && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 mb-4 text-sm text-amber-900" data-testid="yext-mapping-provisional">
          Field mapping is <strong>provisional</strong>. After Yext delivers its first real export, confirm the exact CSV headers below and save. Toto will lock the mapping going forward. Per-boutique overrides live on the <strong>Sources</strong> tab.
        </div>
      )}
      <div className="rounded-2xl border border-stone-100 bg-white p-5 mb-4" data-testid="yext-mapping-editor">
        <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-3">Toto field → Yext CSV header (default)</div>
        <div className="grid grid-cols-1 gap-2 text-sm">
          {Object.entries(doc.candidates || {}).map(([field, candidates]) => (
            <div key={field} className="grid grid-cols-3 gap-2 items-center">
              <span className="font-mono text-xs text-stone-600">{field}</span>
              <input value={confirmed[field] || ""}
                     onChange={(e) => canEdit && setConfirmed({ ...confirmed, [field]: e.target.value })}
                     placeholder="(exact CSV header)"
                     readOnly={!canEdit}
                     data-testid={`yext-map-${field}`}
                     className={`border border-stone-200 rounded-lg px-2 py-1 text-xs col-span-1 ${canEdit ? "" : "bg-stone-50 text-stone-500"}`} />
              <span className="text-[10px] text-stone-400 truncate">Candidates: {candidates.join(", ")}</span>
            </div>
          ))}
        </div>
      </div>
      {canEdit && (
        <button onClick={save} data-testid="yext-mapping-save"
                className="rounded-full bg-stone-900 text-white text-sm px-4 py-2 hover:bg-stone-700">Save & lock mapping</button>
      )}
    </div>
  );
}


// ============================================================
// Preview & Accept Modal — appears after every upload
// ============================================================
const MATCH_LABELS = {
  exact_shared:   { label: "Exact match",    tone: "bg-emerald-50 text-emerald-700" },
  exact_override: { label: "Exact match",    tone: "bg-emerald-50 text-emerald-700" },
  alias:          { label: "Likely match",   tone: "bg-sky-50 text-sky-700" },
  unmapped:       { label: "Unmapped",       tone: "bg-stone-100 text-stone-500" },
  conflict:       { label: "Possible conflict", tone: "bg-amber-50 text-amber-700" },
};

function PreviewModal({ preview, viewer, sources, onClose, onDone }) {
  const source = sources.find((s) => s.boutique_code === preview.boutique_code);
  const canAccept = viewer.role === "owner" || viewer.role === "coo" ||
    (viewer.role === "boutique_manager" && viewer.boutique_code === preview.boutique_code);
  const isCFO = viewer.role === "cfo";

  const suggestion = preview.suggestion || { fields: [], column_conflicts: [], missing_required: [], unmapped_optional: [] };
  const [mapping, setMapping] = useState(() => {
    const m = {};
    suggestion.fields.forEach((f) => { if (f.suggested_header) m[f.toto_field] = f.suggested_header; });
    return m;
  });
  const [submitting, setSubmitting] = useState(null);
  const [confirmOptional, setConfirmOptional] = useState(false);

  const requiredFields = suggestion.fields.filter((f) => f.required).map((f) => f.toto_field);
  const missingRequired = requiredFields.filter((rf) => !(mapping[rf] || "").trim());

  const columnUsage = {};
  Object.entries(mapping).forEach(([tf, h]) => {
    if (!h) return;
    (columnUsage[h] ||= []).push(tf);
  });
  const columnConflicts = Object.entries(columnUsage)
    .filter(([, tfs]) => tfs.length > 1)
    .map(([h, tfs]) => ({ csv_header: h, toto_fields: tfs }));

  const optionalUnmapped = suggestion.fields
    .filter((f) => !f.required && !(mapping[f.toto_field] || "").trim())
    .map((f) => f.toto_field);

  const canProceed =
    canAccept &&
    missingRequired.length === 0 &&
    columnConflicts.length === 0 &&
    (optionalUnmapped.length === 0 || confirmOptional);

  const doAction = async (kind) => {
    if (!canProceed) { toast.error("Resolve required fields and conflicts first."); return; }
    setSubmitting(kind);
    try {
      const endpoint = kind === "import" ? "save-and-import" : "save-mapping";
      const r = await api.post(
        `/yext/imports/preview/${preview.id}/${endpoint}`,
        { mapping, allow_unmapped_optional: optionalUnmapped.length > 0 }
      );
      if (kind === "import") {
        const o = r.data.outcome || {};
        toast.success(
          `${preview.boutique_code} · ${o.status}: +${o.inserted_count ?? 0} inserted · `
          + `${o.updated_count ?? 0} updated · ${o.rejected_count ?? 0} rejected` +
          ((o.collision_count ?? 0) > 0 ? ` · ${o.collision_count} collision` : "")
        );
      } else {
        toast.success("Mapping saved. Future imports for this boutique will use it.");
      }
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.error || e.message);
      toast.error(`Failed: ${msg}`);
    } finally { setSubmitting(null); }
  };

  const doDiscard = async () => {
    try {
      await api.delete(`/yext/imports/preview/${preview.id}`);
      toast.success("Preview discarded.");
      onDone();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 flex items-center justify-center p-4"
         data-testid="yext-preview-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-stone-100 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-stone-400">Preview</div>
            <h2 className="font-serif-display text-2xl text-stone-900" data-testid="yext-preview-title">
              Review field mapping
            </h2>
            <div className="mt-1 text-sm text-stone-500 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 text-stone-800 text-xs px-3 py-1"
                    data-testid="yext-preview-boutique">
                <Building2 size={12} />
                {preview.boutique_code} · {source?.boutique_name || ""}
              </span>
              <span className="text-xs text-stone-500">·</span>
              <span className="text-xs text-stone-500 font-mono">{preview.filename}</span>
              {source?.mapping_confirmed_at && (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                  <CheckCircle2 size={11} /> Previously confirmed
                </span>
              )}
              {!source?.mapping_confirmed_at && (
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-700"
                      data-testid="yext-preview-first-time">
                  <AlertTriangle size={11} /> First-time mapping
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} data-testid="yext-preview-close"
                  className="text-stone-400 hover:text-stone-800 p-1"><X size={18} /></button>
        </div>

        {(missingRequired.length > 0 || columnConflicts.length > 0) && (
          <div className="mx-6 mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900"
               data-testid="yext-preview-warnings">
            {missingRequired.length > 0 && (
              <div><strong>Missing required:</strong> {missingRequired.join(", ")}. Cannot Save until these are mapped.</div>
            )}
            {columnConflicts.length > 0 && (
              <div className="mt-1"><strong>Column conflict:</strong>{" "}
                {columnConflicts.map((c) => `${c.csv_header} → ${c.toto_fields.join(" + ")}`).join(" · ")}
              </div>
            )}
          </div>
        )}

        {isCFO && (
          <div className="mx-6 mt-4 rounded-xl border border-stone-200 bg-stone-50 p-3 text-sm text-stone-700"
               data-testid="yext-preview-cfo-notice">
            You have view-only access. Sample values are visible for oversight, but only an owner, COO, or the boutique's GM may Save the mapping.
          </div>
        )}

        <div className="flex-1 overflow-auto px-6 py-4">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-stone-400 sticky top-0 bg-white">
              <tr>
                <th className="text-left py-2">Toto field</th>
                <th className="text-left py-2">Match</th>
                <th className="text-left py-2">Proposed CSV header</th>
                <th className="text-left py-2">Sample values</th>
              </tr>
            </thead>
            <tbody>
              {suggestion.fields.map((f) => {
                const currentHeader = mapping[f.toto_field] || "";
                const bookmark = MATCH_LABELS[f.match_type] || MATCH_LABELS.unmapped;
                const scopedSamples = currentHeader && preview.sample_rows
                  ? preview.sample_rows.map((r) => r[currentHeader]).filter(Boolean).slice(0, 5)
                  : (f.sample_values || []);
                return (
                  <tr key={f.toto_field} className="border-t border-stone-50 align-top"
                      data-testid={`yext-preview-row-${f.toto_field}`}>
                    <td className="py-2 pr-3 font-mono text-xs text-stone-700 whitespace-nowrap">
                      {f.toto_field}
                      {f.required && <span className="ml-1 text-rose-600">*</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${bookmark.tone}`}
                            data-testid={`yext-preview-match-${f.toto_field}`}>
                        {bookmark.label}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <select
                        value={currentHeader}
                        onChange={(e) => setMapping({ ...mapping, [f.toto_field]: e.target.value })}
                        disabled={!canAccept}
                        data-testid={`yext-preview-header-${f.toto_field}`}
                        className="border border-stone-200 rounded-lg px-2 py-1 text-xs w-full min-w-[180px] bg-white">
                        <option value="">— unmapped —</option>
                        {(preview.headers || []).map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 text-xs text-stone-500">
                      {scopedSamples.length
                        ? <div className="space-y-0.5">{scopedSamples.map((v, i) => (
                            <div key={i} className="truncate max-w-[280px]">{v}</div>
                          ))}</div>
                        : <span className="text-stone-400">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {optionalUnmapped.length > 0 && (
            <label className="mt-4 flex items-start gap-2 text-xs text-stone-600"
                   data-testid="yext-preview-optional-confirm">
              <input type="checkbox" checked={confirmOptional}
                     onChange={(e) => setConfirmOptional(e.target.checked)}
                     className="mt-0.5" />
              <span>
                Some optional fields are unmapped ({optionalUnmapped.join(", ")}). Confirm you want to proceed without them.
              </span>
            </label>
          )}
        </div>

        <div className="border-t border-stone-100 px-6 py-4 bg-stone-50/60">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button onClick={doDiscard} data-testid="yext-preview-discard"
                    className="text-xs text-stone-500 hover:text-stone-800 underline">
              Discard preview
            </button>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex flex-col">
                <button onClick={() => doAction("save")}
                        disabled={!canProceed || submitting !== null}
                        data-testid="yext-preview-save-mapping"
                        className="inline-flex items-center gap-2 rounded-full border border-stone-300 text-stone-800 text-sm px-4 py-2 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                  {submitting === "save" ? "Saving…" : "Save Mapping"}
                </button>
                <div className="text-[10px] text-stone-400 mt-1 max-w-[220px] text-right">
                  Save this field mapping for future Yext files without importing this preview.
                </div>
              </div>
              <div className="flex flex-col">
                <button onClick={() => doAction("import")}
                        disabled={!canProceed || submitting !== null}
                        data-testid="yext-preview-save-and-import"
                        className="inline-flex items-center gap-2 rounded-full bg-stone-900 text-white text-sm px-4 py-2 hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  {submitting === "import" ? "Importing…" : "Save & Import"}
                </button>
                <div className="text-[10px] text-stone-400 mt-1 max-w-[220px] text-right">
                  Save the mapping and import this review file now.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Mapping History Drawer (with rollback)
// ============================================================
function HistoryDrawer({ boutique_code, viewer, onClose, onRolledBack }) {
  const canRollback = viewer.role === "owner" || viewer.role === "coo" ||
    (viewer.role === "boutique_manager" && viewer.boutique_code === boutique_code);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/yext/sources/${boutique_code}/mapping/history`)
      .then((r) => setEntries(r.data))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [boutique_code]);

  const doRollback = async (id) => {
    if (!canRollback) return;
    if (!window.confirm(`Roll back ${boutique_code} mapping to this earlier state?`)) return;
    setRolling(id);
    try {
      await api.post(`/yext/sources/${boutique_code}/mapping/rollback/${id}`);
      toast.success("Mapping rolled back.");
      onRolledBack();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setRolling(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 flex items-center justify-end"
         data-testid="yext-history-drawer" onClick={onClose}>
      <div className="bg-white h-full w-full max-w-md overflow-y-auto shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-stone-100 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-stone-400">Mapping history</div>
            <div className="font-serif-display text-xl text-stone-900">{boutique_code}</div>
          </div>
          <button onClick={onClose} data-testid="yext-history-close"
                  className="text-stone-400 hover:text-stone-800"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-3">
          {loading && <div className="text-sm text-stone-400">Loading…</div>}
          {!loading && entries.length === 0 && (
            <div className="text-sm text-stone-500">No mapping has been accepted yet for this boutique.</div>
          )}
          {entries.map((h) => (
            <div key={h.id}
                 data-testid={`yext-history-entry-${h.id}`}
                 className="rounded-xl border border-stone-100 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${h.action === "rollback" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                  {h.action}
                </span>
                <span className="text-[11px] text-stone-500">{fmt(h.confirmed_at)}</span>
              </div>
              <div className="text-xs text-stone-500 mb-2">by {h.confirmed_by || "—"}</div>
              {Object.keys(h.override || {}).length === 0 ? (
                <div className="text-xs text-stone-400">— uses shared default —</div>
              ) : (
                <div className="text-xs text-stone-700 space-y-0.5">
                  {Object.entries(h.override).map(([k, v]) => (
                    <div key={k}><span className="font-mono">{k}</span> → {v}</div>
                  ))}
                </div>
              )}
              {h.filename && <div className="text-[11px] text-stone-400 mt-2">source file: {h.filename}</div>}
              {canRollback && (
                <div className="mt-3 text-right">
                  <button onClick={() => doRollback(h.id)} disabled={rolling === h.id}
                          data-testid={`yext-history-rollback-${h.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-stone-200 text-stone-700 text-xs px-3 py-1 hover:bg-stone-50">
                    <RotateCcw size={11} /> {rolling === h.id ? "Rolling back…" : "Roll back to this"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

